import type { ComponentProps, ReactElement } from 'react';

import { useTheme } from '@emotion/react';
import { isNil } from 'ramda';

import type { RequireOnlyOne } from '@twistopayments/design-system/utils';

import { BackButton } from '../back-button';
import { Flex } from '../flex';
import { Typography } from '../typography';

import { ModalCloser } from './modal-closer';
import * as styles from './modal-navigation.styles';

type BackButtonProps = RequireOnlyOne<
  {
    to: string;
    onClick: () => void;
  },
  'to' | 'onClick'
>;

type Props = {
  title?: ReactElement | string;
  sideContent?: ReactElement;
  titleId?: string;
  backButtonProps?: BackButtonProps;
  label?: ReactElement | string;
  modalCloser?: ComponentProps<typeof ModalCloser>;
  noSeparator?: boolean;
};

export const ModalNavigation = ({
  title,
  titleId,
  label,
  modalCloser: modalCloserProps,
  backButtonProps,
  noSeparator = false,
  sideContent,
}: Props) => {
  const theme = useTheme();

  return (
    <Flex
      alignItems="center"
      css={[styles.navigation(theme), !noSeparator && styles.separator(theme)]}
      data-testid="modal-navigation"
      justify="spaceBetween"
      wrap="noWrap">
      <Flex alignItems="center" css={styles.adornment} justify="flexStart">
        {backButtonProps && <BackButton {...backButtonProps} css={styles.backButton(theme)} />}
        <div>
          {label !== undefined && (
            <Typography align="start" color="textSecondary" variant="b3">
              {label}
            </Typography>
          )}
          {title !== undefined && (
            <Typography
              align="start"
              color="textPrimary"
              css={styles.title(theme)}
              fontWeight="medium"
              id={titleId}
              variant="b1">
              {title}
            </Typography>
          )}
        </div>
        {!isNil(sideContent) && sideContent}
      </Flex>

      {!isNil(modalCloserProps) && (
        <ModalCloser position="relative" {...modalCloserProps} />
      )}
    </Flex>
  );
};
